import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CreditCard, Search, Filter, Edit, Trash2, Plus } from "lucide-react";
import * as Switch from "@radix-ui/react-switch";
import {
  fetchPaymentModes,
  togglePaymentModeStatus,
  deletePaymentMode,
  resetPaymentModeStatuses,
} from "../../store/slices/paymentModesSlice";
import ReusableModal, { MODAL_TYPES } from "../../components/ReusableModal";
import { showApiError, showApiSuccess } from "../../utils/toast";
import Loader from "../../components/Loader";
import Pagination from "../../components/tables/Pagination";

export default function ViewPaymentModesPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [modalState, setModalState] = useState({
    open: false,
    type: null,
    data: null,
    loading: false,
  });

  const entireState = useSelector((state) => state);
  console.log("ViewPaymentModesPage - ENTIRE Redux state:", entireState);
  console.log("ViewPaymentModesPage - state.paymentModes:", entireState.paymentModes);

  const {
    paymentModes = [],
    pagination,
    paymentModeStatusChangeStatus,
    paymentModeDeleteStatus,
    loading,
  } = useSelector((state) => state.paymentModes || {});

  console.log("ViewPaymentModesPage - paymentModes from state:", paymentModes);
  console.log("ViewPaymentModesPage - paymentModes length:", paymentModes?.length);

  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const itemsPerPage = 10;

  // Calculate which backend page we need
  const backendPageSize = 100;
  const startRecord = (currentPage - 1) * itemsPerPage;
  const backendPage = Math.floor(startRecord / backendPageSize) + 1;

  useEffect(() => {
    console.log("ViewPaymentModesPage - Dispatching fetchPaymentModes");
    const result = dispatch(fetchPaymentModes({ page: backendPage, per_page: backendPageSize }));
    console.log("ViewPaymentModesPage - Dispatch result:", result);
  }, [dispatch, backendPage]);

  // Client-side filtering
  const filteredPaymentModes = paymentModes.filter((paymentMode) => {
    const matchesSearch =
      !searchTerm ||
      (paymentMode.name &&
        paymentMode.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (paymentMode.description &&
        paymentMode.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesFilter =
      filterStatus === "all" ||
      (paymentMode.status ? "active" : "inactive") === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const hasActiveClientFilters = searchTerm !== "" || filterStatus !== "all";

  // Client-side pagination
  const totalRecords = hasActiveClientFilters
    ? filteredPaymentModes.length
    : (pagination?.total || filteredPaymentModes.length);
  const totalPages = Math.ceil(totalRecords / itemsPerPage);
  const backendStartIndex = (backendPage - 1) * backendPageSize;
  const localStartIndex = startRecord - backendStartIndex;
  const localEndIndex = localStartIndex + itemsPerPage;
  const paginatedPaymentModes = filteredPaymentModes.slice(localStartIndex, localEndIndex);

  const handlePageChange = (page) => {
    navigate(`?page=${page}`);
  };

  const openModal = (type, data) => {
    setModalState({
      open: true,
      type,
      data: { ...data, entityType: "Payment Mode" },
      loading: false,
    });
  };

  const closeModal = () => {
    setModalState({
      open: false,
      type: null,
      data: null,
      loading: false,
    });
  };

  const setModalLoading = (loading) => {
    setModalState((prev) => ({ ...prev, loading }));
  };

  const handleStatusChange = async (paymentMode) => {
    setModalLoading(true);
    try {
      const result = await dispatch(togglePaymentModeStatus(paymentMode.uuid));
      if (togglePaymentModeStatus.fulfilled.match(result)) {
        showApiSuccess("Payment mode status updated successfully");
        closeModal();
        dispatch(fetchPaymentModes());
      } else {
        setModalLoading(false);
        showApiError(null, "Failed to update payment mode status");
      }
    } catch (error) {
      console.log("===Console of the ui")
      console.log(error)
      setModalLoading(false);
      showApiError(null, error || "Failed to update payment mode status");
    }
  };

  const handleDeletePaymentMode = async (paymentMode) => {
    setModalLoading(true);
    try {
      const result = await dispatch(deletePaymentMode(paymentMode.uuid));
      if (deletePaymentMode.fulfilled.match(result)) {
        showApiSuccess("Payment mode deleted successfully");
        closeModal();
        dispatch(fetchPaymentModes());
      } else {
        setModalLoading(false);
        showApiError(null, "Failed to delete payment mode");
      }
    } catch (error) {
      setModalLoading(false);
      showApiError(null, error || "Failed to delete payment mode");
    }
  };

  const handleModalConfirm = () => {
    if (modalState.type === MODAL_TYPES.ENABLE_DISABLE) {
      handleStatusChange(modalState.data);
    } else if (modalState.type === MODAL_TYPES.DELETE) {
      handleDeletePaymentMode(modalState.data);
    }
  };

  const handleEditPaymentMode = (paymentMode) => {
    navigate(`/payment-modes/add?edit=${paymentMode.uuid}`);
  };

  useEffect(() => {
    if (paymentModeStatusChangeStatus === "success") {
      dispatch(resetPaymentModeStatuses());
    } else if (paymentModeStatusChangeStatus === "failed") {
      dispatch(resetPaymentModeStatuses());
    }
  }, [paymentModeStatusChangeStatus, dispatch]);

  useEffect(() => {
    if (paymentModeDeleteStatus === "success") {
      dispatch(resetPaymentModeStatuses());
    } else if (paymentModeDeleteStatus === "failed") {
      dispatch(resetPaymentModeStatuses());
    }
  }, [paymentModeDeleteStatus, dispatch]);

  const getStatusBadge = (status) => {
    return status
      ? "bg-green-100 text-green-800"
      : "bg-red-100 text-red-800";
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {loading && <Loader />}

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Payment Modes</h2>
            <p className="text-sm text-gray-500 mt-1">
              Manage payment methods for businesses
            </p>
          </div>
          <button
            onClick={() => navigate("/payment-modes/add")}
            className="mt-4 sm:mt-0 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Add Payment Mode</span>
          </button>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search payment modes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Payment Modes Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedPaymentModes.length > 0 ? (
                paginatedPaymentModes.map((paymentMode) => (
                  <tr key={paymentMode.uuid} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 font-medium text-gray-900">
                      {paymentMode.name}
                    </td>
                    <td className="px-6 py-4 text-gray-500">
                      {paymentMode.description ? (
                        <span className="text-sm">{paymentMode.description}</span>
                      ) : (
                        <span className="text-sm text-gray-400 italic">No description</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(
                          paymentMode.status
                        )}`}
                      >
                        {paymentMode.status ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <Switch.Root
                          id={`payment-mode-status-${paymentMode.uuid}`}
                          checked={paymentMode.status}
                          onCheckedChange={() =>
                            openModal(MODAL_TYPES.ENABLE_DISABLE, paymentMode)
                          }
                          className="w-[42px] h-[25px] bg-gray-200 rounded-full relative shadow-sm 
                               data-[state=checked]:bg-blue-600 outline-none cursor-pointer"
                        >
                          <Switch.Thumb
                            className="block w-[21px] h-[21px] bg-white rounded-full transition-transform 
                                 translate-x-0.5 will-change-transform 
                                 data-[state=checked]:translate-x-[19px]"
                          />
                        </Switch.Root>

                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditPaymentMode(paymentMode)}
                            className="text-green-600 hover:text-green-800 p-1 rounded hover:bg-green-50"
                            title="Edit Payment Mode"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() =>
                              openModal(MODAL_TYPES.DELETE, paymentMode)
                            }
                            className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
                            title="Delete Payment Mode"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    <CreditCard className="mx-auto h-12 w-12 text-gray-300 mb-4" />
                    <p className="text-lg font-medium mb-2">
                      No payment modes found
                    </p>
                    <p className="text-sm">
                      {searchTerm || filterStatus !== "all"
                        ? "Try adjusting your search or filters"
                        : "Get started by creating your first payment mode"}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalRecords}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
        />
      )}

      {/* Reusable Modal */}
      <ReusableModal
        open={modalState.open}
        onOpenChange={closeModal}
        type={modalState.type}
        data={modalState.data}
        onConfirm={handleModalConfirm}
        loading={modalState.loading}
      />
    </div>
  );
}
